import type { LocalDateString, LocalDateTimeString } from '../types/PublicBookingChannelTypes'

export function startOfDay(date: Date): Date {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

export function addDays(date: Date, days: number): Date {
  const d = new Date(date)
  d.setDate(d.getDate() + days)
  return d
}

export function toLocalDateString(date: Date): LocalDateString {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function isSameDay(a: Date | null, b: Date | null): boolean {
  if (!a || !b) return false
  return (
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  )
}

function localDateTimeHasTimezone(value: string): boolean {
  return /([zZ]|[+-]\d{2}:\d{2}|[+-]\d{4})$/.test(value)
}

interface ParsedLocalDateTime {
  year: number
  month: number
  day: number
  hour: number
  minute: number
}

function parseLocalDateTime(value: string): ParsedLocalDateTime | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,9})?)?$/.exec(value)
  if (!match) return null

  const parsed = {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
  }

  return Object.values(parsed).every(Number.isFinite) ? parsed : null
}

function formatTimeFromParts(hour: number, minute: number): string {
  const suffix = hour >= 12 ? 'pm' : 'am'
  const hr12 = hour % 12 === 0 ? 12 : hour % 12
  return `${hr12}:${String(minute).padStart(2, '0')}${suffix}`
}

/**
 * Formats a slot time.
 *
 * The backend sends local date-times without an offset. Parsing those with
 * `new Date()` would reinterpret them in the visitor's timezone and shift the
 * slot — a customer in Spain booking a UK garage would see the wrong hour. So
 * offset-less values are read literally, and only values that carry an offset
 * go through Date.
 */
export function formatSlotTime(value: LocalDateTimeString): string {
  if (localDateTimeHasTimezone(value)) {
    const dt = new Date(value)
    if (Number.isNaN(dt.getTime())) return value
    return formatTimeFromParts(dt.getHours(), dt.getMinutes())
  }

  const parsed = parseLocalDateTime(value)
  return parsed ? formatTimeFromParts(parsed.hour, parsed.minute) : value
}

export function formatSlotDate(value: LocalDateTimeString): string {
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }

  if (localDateTimeHasTimezone(value)) {
    const dt = new Date(value)
    return Number.isNaN(dt.getTime()) ? value : dt.toLocaleDateString('en-GB', options)
  }

  const parsed = parseLocalDateTime(value)
  if (!parsed) return value

  const dt = new Date(parsed.year, parsed.month - 1, parsed.day)
  return Number.isNaN(dt.getTime()) ? value : dt.toLocaleDateString('en-GB', options)
}

export function formatMonthLabel(date: Date): string {
  return date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
}

export function formatDayLabel(date: Date): string {
  return date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })
}

/** Renders pounds without trailing `.00`, which reads as a price rather than an invoice line. */
export function formatMoney(amount: number | null | undefined): string {
  if (amount == null || !Number.isFinite(amount)) return ''
  const rounded = Math.round(amount * 100) / 100
  return Number.isInteger(rounded) ? `£${rounded}` : `£${rounded.toFixed(2)}`
}

/** Always with pence, as refund wording reads: £20.00 (ACA-44). */
export function formatPounds(amount: number): string {
  return `£${amount.toFixed(2)}`
}

export function formatDuration(minutes: number | null | undefined): string {
  if (!minutes || minutes <= 0) return ''
  if (minutes < 60) return `${minutes} min`

  const hours = Math.floor(minutes / 60)
  const remainder = minutes % 60
  if (remainder === 0) return hours === 1 ? '1 hour' : `${hours} hours`
  return `${hours}h ${remainder}m`
}

export function normalizePrice(price: unknown): number | null {
  if (typeof price === 'number' && Number.isFinite(price)) return price
  if (typeof price === 'string') {
    const n = Number(price)
    return Number.isFinite(n) ? n : null
  }
  return null
}

export function sanitizeRegistration(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8)
}
